import { formatSci, parseNumericAnswer, type AcidBaseCheck } from './acidBaseCalculations';
import { calculateMolarMass } from './chemistryCalculations';
import type {
  BackTitrationSpec,
  DirectTitrationSpec,
  PrimaryStandardCase,
  StandardProblem,
  TitrationProblem,
} from './volumetricAnalysisData';

export type VolumetricMode = 'standard' | 'acid-base' | 'back' | 'redox';
export type VolumetricCheck = AcidBaseCheck;

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const VOLUMETRIC_TOOL_IDENTITY = {
  id: 'volumetric-analysis',
  toolKey: 'chemistry-volumetric-analysis',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const VOLUMETRIC_MODES: readonly VolumetricMode[] = [
  'standard',
  'acid-base',
  'back',
  'redox',
];
export const VOLUMETRIC_REVIEW_STATUS = 'pending-teaching-review' as const;
export const VOLUMETRIC_CONDITIONS =
  'Aqueous solutions at 25 °C. Volumes come from calibrated A-grade glassware: a pipette for the aliquot and a burette for the titre.';

export const VOLUMETRIC_SOURCES = [
  {
    label: 'IUPAC Gold Book and Orange Book',
    detail: 'Primary standard, standard solution, titration, end point and equivalence point.',
  },
  {
    label: 'IUPAC standard atomic weights (classroom values)',
    detail: 'Molar masses are computed from the shared Study atomic-mass table.',
  },
  {
    label: 'Standard senior chemistry practical texts',
    detail:
      'Concordant titres agree within 0.10 mL; KMnO₄, I₂/S₂O₃²⁻ and Cr₂O₇²⁻ redox stoichiometry; back-titration method.',
  },
];

export const VOLUMETRIC_MODEL_BOUNDARIES = [
  'Titres are reviewed teaching data, not live measurements. Each set has one rough titre first.',
  'Concordant titres are the largest group of accurate titres that agree within 0.10 mL. The rough titre is never averaged.',
  'The model assumes the end point equals the equivalence point and that no side reactions occur.',
  'This is not a practical guide or risk assessment. Real titrations need teacher supervision, eye protection and correct disposal.',
];

/** Concordant titres agree within this range (mL). */
export const CONCORDANCE_TOLERANCE_ML = 0.1;
export const ML_PER_L = 1000;
export const PERCENT = 100;
/** Final answers are marked to ±0.5 %. */
export const VOLUMETRIC_RELATIVE_TOLERANCE = 0.005;
const FLOAT_EPSILON = 1e-9;
/** Fewest titres that may be averaged. */
export const MIN_CONCORDANT = 2;

// ---------------------------------------------------------------------------------------------
// Concordant titres
// ---------------------------------------------------------------------------------------------

export interface ConcordantResult {
  /** Indices into the titre list (index 0 is the rough titre and is never included). */
  indices: number[];
  average: number;
  spread: number;
}

/**
 * Finds the largest group of accurate titres within 0.10 mL. Ties prefer the smaller spread,
 * then the earlier group. Returns null when fewer than two titres agree: the learner must repeat.
 */
export function findConcordantTitres(titres: readonly number[]): ConcordantResult | null {
  const accurate = titres
    .map((value, index) => ({ value, index }))
    .slice(1)
    .filter((entry) => Number.isFinite(entry.value) && entry.value > 0)
    .sort((left, right) => left.value - right.value);
  let best: { start: number; end: number } | null = null;
  for (let start = 0; start < accurate.length; start += 1) {
    let end = start;
    while (
      end + 1 < accurate.length &&
      accurate[end + 1].value - accurate[start].value <= CONCORDANCE_TOLERANCE_ML + FLOAT_EPSILON
    ) {
      end += 1;
    }
    const size = end - start + 1;
    const spread = accurate[end].value - accurate[start].value;
    if (
      !best ||
      size > best.end - best.start + 1 ||
      (size === best.end - best.start + 1 &&
        spread < accurate[best.end].value - accurate[best.start].value - FLOAT_EPSILON)
    ) {
      best = { start, end };
    }
  }
  if (!best || best.end - best.start + 1 < MIN_CONCORDANT) return null;
  const group = accurate.slice(best.start, best.end + 1);
  return {
    indices: group.map((entry) => entry.index).sort((left, right) => left - right),
    average: group.reduce((total, entry) => total + entry.value, 0) / group.length,
    spread: accurate[best.end].value - accurate[best.start].value,
  };
}

const formatMl = (value: number) => value.toFixed(2);

export function checkConcordantSelection(
  titres: readonly number[],
  selected: readonly number[],
): VolumetricCheck {
  const model = findConcordantTitres(titres);
  if (!model) {
    return {
      status: 'invalid',
      title: 'No concordant titres',
      feedback:
        'No two accurate titres agree within 0.10 mL. Repeat the titration before averaging.',
    };
  }
  if (selected.length === 0) {
    return {
      status: 'invalid',
      title: 'Choose titres',
      feedback: 'Tick the titres you would average.',
    };
  }
  const averageText = `Average = ${model.indices.map((index) => formatMl(titres[index])).join(' + ')} ÷ ${model.indices.length} = ${formatMl(model.average)} mL.`;
  if (selected.includes(0)) {
    return {
      status: 'misconception',
      title: 'Not yet — leave out the rough titre',
      feedback:
        'The rough titre is a quick first run to find roughly where the end point is. It usually overshoots, so it is never averaged.',
    };
  }
  const values = selected.map((index) => titres[index]);
  const spread = Math.max(...values) - Math.min(...values);
  if (selected.length < MIN_CONCORDANT) {
    return {
      status: 'misconception',
      title: 'Not yet — one titre is not enough',
      feedback: 'Concordant means at least two accurate titres that agree within 0.10 mL.',
    };
  }
  if (spread > CONCORDANCE_TOLERANCE_ML + FLOAT_EPSILON) {
    const high = Math.max(...values);
    const low = Math.min(...values);
    return {
      status: 'misconception',
      title: 'Not yet — these titres do not agree',
      feedback: `${formatMl(high)} mL and ${formatMl(low)} mL differ by ${formatMl(spread)} mL, which is more than 0.10 mL. Leave out the titre that does not fit.`,
    };
  }
  const sameSet =
    selected.length === model.indices.length &&
    model.indices.every((index) => selected.includes(index));
  if (sameSet) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `These ${model.indices.length} titres agree within 0.10 mL (spread ${formatMl(model.spread)} mL). ${averageText}`,
    };
  }
  const missing = model.indices.filter((index) => !selected.includes(index));
  if (missing.length > 0 && selected.every((index) => model.indices.includes(index))) {
    return {
      status: 'misconception',
      title: 'Not yet — use every concordant titre',
      feedback: `${missing.map((index) => `${formatMl(titres[index])} mL`).join(' and ')} also agrees within 0.10 mL of your choices. Averaging more concordant results reduces random error.`,
    };
  }
  return {
    status: 'misconception',
    title: 'Not yet — a larger group agrees',
    feedback: `Your titres agree, but a group of ${model.indices.length} titres also agrees within 0.10 mL. Use the largest concordant group.`,
  };
}

// ---------------------------------------------------------------------------------------------
// Molar mass and shared answer marking
// ---------------------------------------------------------------------------------------------

export function molarMassOf(formula: string): number | null {
  const result = calculateMolarMass(formula);
  return result.value ? result.value.molarMass : null;
}

export interface NumericMisconception {
  value: number;
  feedback: string;
}

export interface FlowStep {
  label: string;
  value: string;
}

export interface VolumetricSolution {
  value: number;
  unit: string;
  quantityLabel: string;
  working: string[];
  /** Mole-map boxes, in order, for the reveal visual. */
  flow: FlowStep[];
  misconceptions: NumericMisconception[];
  correctReason: string;
  concordant: ConcordantResult | null;
}

export type VolumetricResult =
  { value: VolumetricSolution; error: null } | { value: null; error: string };

const fail = (error: string): VolumetricResult => ({ value: null, error });
const ok = (value: VolumetricSolution): VolumetricResult => ({ value, error: null });
const isPositive = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value) && value > 0;
const litres = (ml: number) => ml / ML_PER_L;
const mol = (value: number) => `${formatSci(value, 4)} mol`;
const molar = (value: number) => `${formatSci(value, 4)} mol L⁻¹`;

export function withinVolumetricTolerance(answer: number, expected: number): boolean {
  if (!Number.isFinite(answer) || !Number.isFinite(expected) || expected === 0) return false;
  return Math.abs(answer - expected) <= Math.abs(expected) * VOLUMETRIC_RELATIVE_TOLERANCE;
}

export function markVolumetricAnswer(result: VolumetricResult, rawAnswer: string): VolumetricCheck {
  if (!result.value)
    return { status: 'invalid', title: 'Problem unavailable', feedback: result.error };
  const answer = parseNumericAnswer(rawAnswer);
  if (!Number.isFinite(answer)) {
    return {
      status: 'invalid',
      title: 'Enter a number',
      feedback: 'Type a number such as 0.1095 or 96.1, without units.',
    };
  }
  const { value, unit, quantityLabel, misconceptions, correctReason } = result.value;
  if (withinVolumetricTolerance(answer, value)) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `${correctReason} ${quantityLabel} = ${formatSci(value, 4)} ${unit}.`,
    };
  }
  const diagnosed = misconceptions.find(
    (item) =>
      Number.isFinite(item.value) &&
      !withinVolumetricTolerance(item.value, value) &&
      withinVolumetricTolerance(answer, item.value),
  );
  if (diagnosed) {
    return {
      status: 'misconception',
      title: 'Not yet — check your method',
      feedback: diagnosed.feedback,
    };
  }
  return {
    status: 'incorrect',
    title: 'Not yet',
    feedback: `Your answer is ${answer > value ? 'too large' : 'too small'}. The model gives ${quantityLabel} = ${formatSci(value, 4)} ${unit}. Compare each step of the working with yours.`,
  };
}

const UNIT_MISCONCEPTIONS = (value: number): NumericMisconception[] => [
  {
    value: value * ML_PER_L,
    feedback: 'Convert millilitres to litres (÷ 1000) before you use n = c × V or c = n ÷ V.',
  },
  {
    value: value / ML_PER_L,
    feedback:
      'Check the volume unit: one step used litres where the formula needed millilitres, or the reverse.',
  },
];

// ---------------------------------------------------------------------------------------------
// Standard solutions
// ---------------------------------------------------------------------------------------------

export function checkPrimaryStandard(
  item: PrimaryStandardCase,
  suitable: boolean,
): VolumetricCheck {
  if (suitable === item.suitable) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `${item.name} ${item.suitable ? 'is' : 'is not'} a primary standard. ${item.reason}`,
    };
  }
  return {
    status: 'misconception',
    title: 'Not yet',
    feedback: `${item.name} ${item.suitable ? 'is' : 'is not'} a primary standard. ${item.reason} A primary standard must be very pure, stable in air, not hygroscopic, of exactly known formula and preferably of high molar mass.`,
  };
}

export function solveStandardProblem(problem: StandardProblem): VolumetricResult {
  const { spec } = problem;
  switch (spec.kind) {
    case 'mass-needed': {
      const molarMass = molarMassOf(spec.formula);
      if (!molarMass) return fail('No molar mass is available for this substance.');
      if (!isPositive(spec.concentration) || !isPositive(spec.volumeMl)) {
        return fail('Concentration and volume must be above zero.');
      }
      const amount = spec.concentration * litres(spec.volumeMl);
      const mass = amount * molarMass;
      const anhydrous = spec.anhydrousFormula ? molarMassOf(spec.anhydrousFormula) : null;
      return ok({
        value: mass,
        unit: 'g',
        quantityLabel: 'mass',
        working: [
          `n = c × V = ${formatSci(spec.concentration, 4)} mol L⁻¹ × ${formatSci(litres(spec.volumeMl), 4)} L = ${mol(amount)}`,
          `M(${problem.display}) = ${molarMass.toFixed(2)} g mol⁻¹`,
          `m = n × M = ${mol(amount)} × ${molarMass.toFixed(2)} g mol⁻¹ = ${formatSci(mass, 4)} g`,
        ],
        flow: [
          { label: 'c × V', value: mol(amount) },
          { label: '× M', value: `${formatSci(mass, 4)} g` },
        ],
        misconceptions: [
          ...UNIT_MISCONCEPTIONS(mass),
          ...(anhydrous
            ? [
                {
                  value: amount * anhydrous,
                  feedback: `You used the anhydrous molar mass. The solid weighed out is the hydrate ${problem.display}, so its water of crystallisation counts: M = ${molarMass.toFixed(2)} g mol⁻¹.`,
                },
              ]
            : []),
        ],
        correctReason:
          'Weigh this mass accurately, dissolve it and make it up to the mark in a volumetric flask.',
        concordant: null,
      });
    }
    case 'concentration-from-mass': {
      const molarMass = molarMassOf(spec.formula);
      if (!molarMass) return fail('No molar mass is available for this substance.');
      if (!isPositive(spec.massG) || !isPositive(spec.volumeMl)) {
        return fail('Mass and volume must be above zero.');
      }
      const amount = spec.massG / molarMass;
      const concentration = amount / litres(spec.volumeMl);
      return ok({
        value: concentration,
        unit: 'mol L⁻¹',
        quantityLabel: 'c',
        working: [
          `n = m ÷ M = ${spec.massG} g ÷ ${molarMass.toFixed(2)} g mol⁻¹ = ${mol(amount)}`,
          `c = n ÷ V = ${mol(amount)} ÷ ${formatSci(litres(spec.volumeMl), 4)} L = ${molar(concentration)}`,
        ],
        flow: [
          { label: 'm ÷ M', value: mol(amount) },
          { label: '÷ V', value: molar(concentration) },
        ],
        misconceptions: [
          ...UNIT_MISCONCEPTIONS(concentration),
          {
            value: spec.massG / litres(spec.volumeMl),
            feedback:
              'That is a mass concentration in g L⁻¹. Convert grams to moles with n = m ÷ M first.',
          },
        ],
        correctReason:
          'A primary standard weighed accurately gives a concentration known to four significant figures.',
        concordant: null,
      });
    }
    case 'dilution': {
      if (![spec.concentration, spec.aliquotMl, spec.finalMl].every(isPositive)) {
        return fail('Concentration and volumes must be above zero.');
      }
      if (spec.finalMl < spec.aliquotMl) return fail('A dilution cannot make the volume smaller.');
      const diluted = (spec.concentration * spec.aliquotMl) / spec.finalMl;
      return ok({
        value: diluted,
        unit: 'mol L⁻¹',
        quantityLabel: 'c₂',
        working: [
          'c₁V₁ = c₂V₂ (the amount of solute does not change)',
          `c₂ = ${formatSci(spec.concentration, 4)} × ${spec.aliquotMl} mL ÷ ${spec.finalMl} mL = ${molar(diluted)}`,
        ],
        flow: [
          { label: 'n = c₁V₁', value: mol(spec.concentration * litres(spec.aliquotMl)) },
          { label: '÷ V₂', value: molar(diluted) },
        ],
        misconceptions: [
          {
            value: (spec.concentration * spec.finalMl) / spec.aliquotMl,
            feedback:
              'The ratio is upside down. Adding water lowers the concentration: c₂ = c₁V₁ ÷ V₂.',
          },
        ],
        correctReason: `Diluting ${spec.aliquotMl} mL to ${spec.finalMl} mL lowers the concentration by a factor of ${formatSci(spec.finalMl / spec.aliquotMl)}.`,
        concordant: null,
      });
    }
    default:
      return fail('Unsupported standard-solution problem.');
  }
}

// ---------------------------------------------------------------------------------------------
// Direct titrations (acid–base and redox)
// ---------------------------------------------------------------------------------------------

interface DirectTreatment {
  averageAll?: boolean;
  ratio?: 'one-to-one' | 'inverted';
  ignoreScale?: boolean;
}

function averageAllTitres(titres: readonly number[]): number {
  return titres.reduce((total, value) => total + value, 0) / titres.length;
}

function directValue(
  spec: DirectTitrationSpec,
  titre: number,
  treatment: DirectTreatment,
): number | null {
  const { titrant: titrantCoefficient, analyte: analyteCoefficient } = spec.coefficients;
  const analytePerTitrant =
    treatment.ratio === 'one-to-one'
      ? 1
      : treatment.ratio === 'inverted'
        ? titrantCoefficient / analyteCoefficient
        : analyteCoefficient / titrantCoefficient;
  const find = spec.find;
  if (find.quantity === 'titrant-concentration') {
    if (!isPositive(spec.analyte.concentration)) return null;
    const analyteMol = spec.analyte.concentration * litres(spec.aliquotMl);
    return analyteMol / analytePerTitrant / litres(titre);
  }
  if (!isPositive(spec.titrant.concentration)) return null;
  const analyteMol = spec.titrant.concentration * litres(titre) * analytePerTitrant;
  const aliquotConcentration = analyteMol / litres(spec.aliquotMl);
  if (find.quantity === 'analyte-concentration') return aliquotConcentration;
  if (find.quantity === 'original-concentration') {
    return treatment.ignoreScale
      ? aliquotConcentration
      : (aliquotConcentration * find.flaskMl) / find.sampleMl;
  }
  const molarMass = molarMassOf(find.molarFormula);
  if (!molarMass) return null;
  const scale = treatment.ignoreScale ? 1 : find.flaskMl / spec.aliquotMl;
  return ((analyteMol * scale * molarMass) / find.sampleMassG) * PERCENT;
}

export function solveDirectTitration(problem: TitrationProblem): VolumetricResult {
  const spec = problem.spec;
  if (spec.kind !== 'direct') return fail('This is not a direct titration.');
  if (
    !isPositive(spec.aliquotMl) ||
    !isPositive(spec.coefficients.titrant) ||
    !isPositive(spec.coefficients.analyte)
  ) {
    return fail('Aliquot volume and mole ratio must be above zero.');
  }
  const concordant = findConcordantTitres(spec.titres);
  if (!concordant)
    return fail('No two accurate titres agree within 0.10 mL. Repeat the titration.');
  const titre = concordant.average;
  const value = directValue(spec, titre, {});
  if (value === null || !Number.isFinite(value))
    return fail('This titration is missing a known concentration or molar mass.');

  const { titrant, analyte, coefficients, find } = spec;
  const ratioText = `${coefficients.analyte} : ${coefficients.titrant}`;
  const working: string[] = [
    `Concordant titres: ${concordant.indices.map((index) => formatMl(spec.titres[index])).join(', ')} mL → average ${formatMl(titre)} mL`,
  ];
  const flow: FlowStep[] = [];
  let quantityLabel = '';
  let unit = 'mol L⁻¹';
  if (find.quantity === 'titrant-concentration' && analyte.concentration) {
    const analyteMol = analyte.concentration * litres(spec.aliquotMl);
    const titrantMol = (analyteMol * coefficients.titrant) / coefficients.analyte;
    quantityLabel = `c(${titrant.formula})`;
    working.push(
      `n(${analyte.formula}) = ${formatSci(analyte.concentration, 4)} mol L⁻¹ × ${formatSci(litres(spec.aliquotMl), 4)} L = ${mol(analyteMol)}`,
      `n(${titrant.formula}) = n(${analyte.formula}) × ${coefficients.titrant}/${coefficients.analyte} = ${mol(titrantMol)}`,
      `c(${titrant.formula}) = ${mol(titrantMol)} ÷ ${formatSci(litres(titre), 4)} L = ${molar(value)}`,
    );
    flow.push(
      { label: `n(${analyte.formula}) = cV`, value: mol(analyteMol) },
      { label: `× ${coefficients.titrant}/${coefficients.analyte}`, value: mol(titrantMol) },
      { label: '÷ average titre', value: molar(value) },
    );
  } else if (titrant.concentration) {
    const titrantMol = titrant.concentration * litres(titre);
    const analyteMol = (titrantMol * coefficients.analyte) / coefficients.titrant;
    const aliquotConcentration = analyteMol / litres(spec.aliquotMl);
    working.push(
      `n(${titrant.formula}) = ${formatSci(titrant.concentration, 4)} mol L⁻¹ × ${formatSci(litres(titre), 4)} L = ${mol(titrantMol)}`,
      `n(${analyte.formula}) = n(${titrant.formula}) × ${coefficients.analyte}/${coefficients.titrant} (mole ratio ${analyte.formula} : ${titrant.formula} = ${ratioText}) = ${mol(analyteMol)}`,
    );
    flow.push(
      { label: `n(${titrant.formula}) = cV`, value: mol(titrantMol) },
      { label: `× ${coefficients.analyte}/${coefficients.titrant}`, value: mol(analyteMol) },
    );
    if (find.quantity === 'analyte-concentration') {
      quantityLabel = `c(${analyte.formula})`;
      working.push(
        `c(${analyte.formula}) = ${mol(analyteMol)} ÷ ${formatSci(litres(spec.aliquotMl), 4)} L = ${molar(value)}`,
      );
      flow.push({ label: '÷ aliquot volume', value: molar(value) });
    } else if (find.quantity === 'original-concentration') {
      quantityLabel = `c(${analyte.formula}) in the original sample`;
      working.push(
        `c(aliquot) = ${mol(analyteMol)} ÷ ${formatSci(litres(spec.aliquotMl), 4)} L = ${molar(aliquotConcentration)}`,
        `Dilution factor = ${find.flaskMl} mL ÷ ${find.sampleMl} mL = ${formatSci(find.flaskMl / find.sampleMl)}`,
        `c(original) = ${molar(aliquotConcentration)} × ${formatSci(find.flaskMl / find.sampleMl)} = ${molar(value)}`,
      );
      flow.push(
        { label: '÷ aliquot volume', value: molar(aliquotConcentration) },
        {
          label: `× dilution factor ${formatSci(find.flaskMl / find.sampleMl)}`,
          value: molar(value),
        },
      );
    } else if (find.quantity === 'mass-percent') {
      const scale = find.flaskMl / spec.aliquotMl;
      const molarMass = molarMassOf(find.molarFormula) ?? 0;
      const totalMol = analyteMol * scale;
      quantityLabel = `%(m/m) ${find.analyteLabel}`;
      unit = '%';
      working.push(
        `Whole flask: n = ${mol(analyteMol)} × ${find.flaskMl} mL ÷ ${spec.aliquotMl} mL = ${mol(totalMol)}`,
        `m = n × M = ${mol(totalMol)} × ${molarMass.toFixed(2)} g mol⁻¹ = ${formatSci(totalMol * molarMass, 4)} g`,
        `% = ${formatSci(totalMol * molarMass, 4)} g ÷ ${find.sampleMassG} g × 100 = ${formatSci(value, 4)} %`,
      );
      flow.push(
        { label: `× ${find.flaskMl}/${spec.aliquotMl} (whole flask)`, value: mol(totalMol) },
        { label: '× M', value: `${formatSci(totalMol * molarMass, 4)} g` },
        { label: '÷ sample mass × 100', value: `${formatSci(value, 4)} %` },
      );
    }
  }

  const misconceptions: NumericMisconception[] = [];
  const allValue = directValue(spec, averageAllTitres(spec.titres), {});
  if (allValue !== null) {
    misconceptions.push({
      value: allValue,
      feedback:
        'You averaged every titre, including the rough one or a titre that does not agree. Average only the concordant titres (within 0.10 mL).',
    });
  }
  if (coefficients.titrant !== coefficients.analyte) {
    const oneToOne = directValue(spec, titre, { ratio: 'one-to-one' });
    const inverted = directValue(spec, titre, { ratio: 'inverted' });
    if (oneToOne !== null) {
      misconceptions.push({
        value: oneToOne,
        feedback: `Use the mole ratio from the balanced equation: ${analyte.formula} : ${titrant.formula} = ${ratioText}, not 1 : 1.`,
      });
    }
    if (inverted !== null) {
      misconceptions.push({
        value: inverted,
        feedback: `The mole ratio is upside down. From the equation, n(${analyte.formula}) = n(${titrant.formula}) × ${coefficients.analyte}/${coefficients.titrant}.`,
      });
    }
  }
  if (find.quantity === 'original-concentration' || find.quantity === 'mass-percent') {
    const unscaled = directValue(spec, titre, { ignoreScale: true });
    if (unscaled !== null) {
      misconceptions.push({
        value: unscaled,
        feedback:
          find.quantity === 'original-concentration'
            ? `That is the concentration of the diluted solution in the flask. Multiply by the dilution factor (${find.flaskMl} ÷ ${find.sampleMl}) to get the original.`
            : `The aliquot holds only ${spec.aliquotMl} mL of the ${find.flaskMl} mL flask. Scale the moles up by ${find.flaskMl} ÷ ${spec.aliquotMl} before finding the mass.`,
      });
    }
  }
  misconceptions.push(...UNIT_MISCONCEPTIONS(value));

  return ok({
    value,
    unit,
    quantityLabel,
    working,
    flow,
    misconceptions,
    correctReason: `Average titre → moles of ${titrant.formula} → mole ratio ${ratioText} → ${analyte.formula}.`,
    concordant,
  });
}

// ---------------------------------------------------------------------------------------------
// Back titrations
// ---------------------------------------------------------------------------------------------

interface BackTreatment {
  averageAll?: boolean;
  noSubtraction?: boolean;
  ignoreTitration?: boolean;
  ratioOne?: boolean;
  ignoreScale?: boolean;
}

function backValue(
  spec: BackTitrationSpec,
  titre: number,
  treatment: BackTreatment,
): number | null {
  const molarMass = molarMassOf(spec.sample.molarFormula);
  if (!molarMass) return null;
  const totalExcess = spec.excess.concentration * litres(spec.excess.volumeMl);
  const titrantMol = spec.titrant.concentration * litres(titre);
  const scale =
    spec.aliquot && !treatment.ignoreScale ? spec.aliquot.flaskMl / spec.aliquot.aliquotMl : 1;
  const leftover = titrantMol * spec.coefficients.excessPerTitrant * scale;
  const reacted = treatment.noSubtraction
    ? leftover
    : treatment.ignoreTitration
      ? totalExcess
      : totalExcess - leftover;
  const ratio = treatment.ratioOne ? 1 : spec.coefficients.analytePerExcess;
  return ((reacted * ratio * molarMass) / spec.sample.massG) * PERCENT;
}

export function solveBackTitration(problem: TitrationProblem): VolumetricResult {
  const spec = problem.spec;
  if (spec.kind !== 'back') return fail('This is not a back titration.');
  const molarMass = molarMassOf(spec.sample.molarFormula);
  if (!molarMass) return fail('No molar mass is available for the sample.');
  const concordant = findConcordantTitres(spec.titres);
  if (!concordant)
    return fail('No two accurate titres agree within 0.10 mL. Repeat the titration.');
  const titre = concordant.average;
  const totalExcess = spec.excess.concentration * litres(spec.excess.volumeMl);
  const titrantMol = spec.titrant.concentration * litres(titre);
  const scale = spec.aliquot ? spec.aliquot.flaskMl / spec.aliquot.aliquotMl : 1;
  const leftover = titrantMol * spec.coefficients.excessPerTitrant * scale;
  const reacted = totalExcess - leftover;
  if (!(reacted > 0)) return fail('The titration shows no reagent was used up. Check the data.');
  const analyteMol = reacted * spec.coefficients.analytePerExcess;
  const mass = analyteMol * molarMass;
  const value = (mass / spec.sample.massG) * PERCENT;
  if (value > PERCENT + FLOAT_EPSILON) return fail('The result is above 100 %. Check the data.');
  const excessF = spec.excess.formula;
  const titrantF = spec.titrant.formula;

  const working = [
    `Step 1 — total ${excessF} added: ${formatSci(spec.excess.concentration, 4)} mol L⁻¹ × ${formatSci(litres(spec.excess.volumeMl), 4)} L = ${mol(totalExcess)}`,
    `Step 2 — concordant titres ${concordant.indices.map((index) => formatMl(spec.titres[index])).join(', ')} mL → average ${formatMl(titre)} mL; n(${titrantF}) = ${mol(titrantMol)}`,
    spec.aliquot
      ? `Step 3 — leftover ${excessF} in the whole flask = ${mol(titrantMol)} × ${spec.coefficients.excessPerTitrant} × ${spec.aliquot.flaskMl}/${spec.aliquot.aliquotMl} = ${mol(leftover)}`
      : `Step 3 — leftover ${excessF} = ${mol(titrantMol)} × ${spec.coefficients.excessPerTitrant} = ${mol(leftover)}`,
    `Step 4 — ${excessF} that reacted with the sample = ${mol(totalExcess)} − ${mol(leftover)} = ${mol(reacted)}`,
    `Step 5 — n(${spec.sample.formula}) = ${mol(reacted)} × ${spec.coefficients.analytePerExcess} = ${mol(analyteMol)}; m = ${formatSci(mass, 4)} g`,
    `% ${spec.sample.formula} = ${formatSci(mass, 4)} g ÷ ${spec.sample.massG} g × 100 = ${formatSci(value, 4)} %`,
  ];
  const flow: FlowStep[] = [
    { label: `${excessF} added`, value: mol(totalExcess) },
    { label: `− leftover (from titre)`, value: mol(leftover) },
    { label: '= reacted with sample', value: mol(reacted) },
    {
      label: `× ${spec.coefficients.analytePerExcess} → ${spec.sample.formula}`,
      value: mol(analyteMol),
    },
    { label: '× M ÷ sample mass × 100', value: `${formatSci(value, 4)} %` },
  ];
  const treatments: { treatment: BackTreatment; feedback: string }[] = [
    {
      treatment: { noSubtraction: true },
      feedback: `The titre measures only the leftover ${excessF}. Subtract it from the total ${excessF} added to find the amount that reacted with the sample.`,
    },
    {
      treatment: { ignoreTitration: true },
      feedback: `Not all of the ${excessF} reacted with the sample: it was added in excess. The titration measures how much was left over.`,
    },
    {
      treatment: { ignoreScale: true },
      feedback: spec.aliquot
        ? `Only ${spec.aliquot.aliquotMl} mL of the ${spec.aliquot.flaskMl} mL flask was titrated. Scale the leftover up before you subtract.`
        : '',
    },
    {
      treatment: { ratioOne: true },
      feedback: `Use the mole ratio from the first equation: 1 mol of ${spec.sample.formula} uses ${formatSci(1 / spec.coefficients.analytePerExcess)} mol of ${excessF}.`,
    },
  ];
  const misconceptions = treatments
    .filter((item) => item.feedback)
    .map((item) => ({
      value: backValue(spec, titre, item.treatment) ?? Number.NaN,
      feedback: item.feedback,
    }));
  const allValue = backValue(spec, averageAllTitres(spec.titres), {});
  if (allValue !== null) {
    misconceptions.push({
      value: allValue,
      feedback:
        'You averaged every titre, including the rough one. Average only the concordant titres.',
    });
  }
  return ok({
    value,
    unit: '%',
    quantityLabel: `%(m/m) ${spec.sample.formula}`,
    working,
    flow,
    misconceptions,
    correctReason: `A back titration works backwards: total ${excessF} − leftover ${excessF} = the amount the sample used.`,
    concordant,
  });
}

export function solveTitration(problem: TitrationProblem): VolumetricResult {
  return problem.spec.kind === 'back' ? solveBackTitration(problem) : solveDirectTitration(problem);
}
